import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL as API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Ticket, Search, Filter, Clock, CheckCircle, AlertCircle, 
  XCircle, MessageSquare, User, Mail, Calendar, ChevronDown,
  ChevronUp, RefreshCw, Loader2
} from 'lucide-react';


const STATUS_COLORS = {
  open: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const STATUS_ICONS = {
  open: AlertCircle,
  in_progress: Clock,
  resolved: CheckCircle,
  closed: XCircle
};

const CATEGORY_LABELS = {
  support: 'General Support',
  artist: 'Artist Question',
  partnership: 'Partnership',
  press: 'Press',
  legal: 'Legal',
  other: 'Other'
};

export default function AdminSupportTicketsPage() {
  const { user, isAdmin, getAuthHeaders, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    search: ''
  });
  const [adminNote, setAdminNote] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    // Wait for auth to load before checking admin status
    if (authLoading) return;
    
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchTickets();
  }, [isAdmin, authLoading, navigate]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/support-tickets`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTickets(data);
      } else {
        toast.error('Failed to fetch tickets');
      }
    } catch (error) {
      toast.error('Error loading tickets');
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await fetch(`${API}/api/admin/support-tickets/${ticketId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          status: newStatus,
          admin_note: adminNote || undefined
        })
      });

      if (response.ok) {
        toast.success('Ticket updated');
        setAdminNote('');
        fetchTickets();
        if (selectedTicket?.ticket_id === ticketId) {
          const updated = await response.json();
          setSelectedTicket(updated);
        }
      } else {
        toast.error('Failed to update ticket');
      }
    } catch (error) {
      toast.error('Error updating ticket');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filters.status !== 'all' && ticket.status !== filters.status) return false;
    if (filters.category !== 'all' && ticket.subject !== filters.category) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        ticket.ticket_id.toLowerCase().includes(search) ||
        ticket.name.toLowerCase().includes(search) ||
        ticket.email.toLowerCase().includes(search) ||
        ticket.message.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length
  };

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="admin-tickets-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase flex items-center gap-2">
              <Ticket className="w-8 h-8 text-secondary" />
              Support Tickets
            </h1>
            <p className="text-muted-foreground mt-1">Manage user support requests</p>
          </div>
          <Button onClick={fetchTickets} variant="outline" className="border-border">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card border-border p-4 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total</p>
          </Card>
          <Card className="glass-card border-amber-500/30 p-4 text-center bg-amber-500/5">
            <p className="text-2xl font-bold text-amber-400">{stats.open}</p>
            <p className="text-sm text-muted-foreground">Open</p>
          </Card>
          <Card className="glass-card border-blue-500/30 p-4 text-center bg-blue-500/5">
            <p className="text-2xl font-bold text-blue-400">{stats.in_progress}</p>
            <p className="text-sm text-muted-foreground">In Progress</p>
          </Card>
          <Card className="glass-card border-green-500/30 p-4 text-center bg-green-500/5">
            <p className="text-2xl font-bold text-green-400">{stats.resolved}</p>
            <p className="text-sm text-muted-foreground">Resolved</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="glass-card border-border p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search tickets..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
              />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="support">Support</SelectItem>
                <SelectItem value="artist">Artist</SelectItem>
                <SelectItem value="partnership">Partnership</SelectItem>
                <SelectItem value="press">Press</SelectItem>
                <SelectItem value="legal">Legal</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Tickets List */}
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-secondary" />
                <p className="text-muted-foreground mt-2">Loading tickets...</p>
              </div>
            ) : filteredTickets.length === 0 ? (
              <Card className="glass-card border-border p-8 text-center">
                <Ticket className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No tickets found</p>
              </Card>
            ) : (
              filteredTickets.map(ticket => {
                const StatusIcon = STATUS_ICONS[ticket.status];
                return (
                  <Card 
                    key={ticket.ticket_id}
                    className={`glass-card border-border p-4 cursor-pointer transition-all hover:border-secondary/50 ${
                      selectedTicket?.ticket_id === ticket.ticket_id ? 'border-secondary' : ''
                    }`}
                    onClick={() => setSelectedTicket(ticket)}
                    data-testid={`ticket-${ticket.ticket_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm text-secondary">{ticket.ticket_id}</span>
                          <Badge className={STATUS_COLORS[ticket.status]}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {ticket.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="font-medium truncate">{ticket.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{ticket.email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {CATEGORY_LABELS[ticket.subject]} • {new Date(ticket.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </div>

          {/* Ticket Detail */}
          <div>
            {selectedTicket ? (
              <Card className="glass-card border-border p-6 sticky top-4" data-testid="ticket-detail">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-mono text-lg text-secondary font-bold">{selectedTicket.ticket_id}</p>
                    <Badge className={`mt-2 ${STATUS_COLORS[selectedTicket.status]}`}>
                      {selectedTicket.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <Badge variant="outline">{CATEGORY_LABELS[selectedTicket.subject]}</Badge>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedTicket.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${selectedTicket.email}`} className="text-secondary hover:underline">
                      {selectedTicket.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>{new Date(selectedTicket.created_at).toLocaleString()}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground mb-2">Message</h3>
                  <div className="p-4 rounded-lg bg-muted/30 border border-border">
                    <p className="text-sm whitespace-pre-wrap">{selectedTicket.message}</p>
                  </div>
                </div>

                {/* Admin Notes */}
                {selectedTicket.admin_notes?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground mb-2">Admin Notes</h3>
                    <div className="space-y-2">
                      {selectedTicket.admin_notes.map((note, i) => (
                        <div key={i} className="p-3 rounded-lg bg-secondary/10 border border-secondary/30 text-sm">
                          <p>{note.note}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {note.admin_name} • {new Date(note.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Update Status */}
                <div className="space-y-4 pt-4 border-t border-border">
                  <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground">Update Ticket</h3>
                  
                  <Textarea
                    placeholder="Add internal note (optional)..."
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    rows={2}
                  />

                  <div className="flex flex-wrap gap-2">
                    {selectedTicket.status !== 'in_progress' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        onClick={() => updateTicketStatus(selectedTicket.ticket_id, 'in_progress')}
                        disabled={updatingStatus}
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        In Progress
                      </Button>
                    )}
                    {selectedTicket.status !== 'resolved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                        onClick={() => updateTicketStatus(selectedTicket.ticket_id, 'resolved')}
                        disabled={updatingStatus}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Resolve
                      </Button>
                    )}
                    {selectedTicket.status !== 'closed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-gray-500/50 text-gray-400 hover:bg-gray-500/10"
                        onClick={() => updateTicketStatus(selectedTicket.ticket_id, 'closed')}
                        disabled={updatingStatus}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Close
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="glass-card border-border p-8 text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Select a ticket to view details</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
